<template>
    <div>
        <TitleBar>
            <template v-slot:title>
                <span v-translate>Intracommunautaire Prestaties</span>
            </template>
            <template v-slot:subtitle>
                <div style="color: #37A0CD">
                    <translate>Calculated on:</translate>&nbsp;
                    <span v-if="report.modified != null">{{ report.modified | formatDate }}</span>
                </div>
            </template>
            <template v-slot:buttons>
                <GekkoButton :text="calculatedStatus" type="button" variant="outline-primary" class="mr-2"
                            @click="handleSubmit" />
                <GekkoButton :text="$gettext('Back')" type="button" icon="fa-solid fa-chart-column" :to="{ name: 'EUServicesList'}" />
            </template>
        </TitleBar>
        <div class="content">
            <LoadingSpinner v-if="loading" />
            <b-modal id="modal-multi-premium" size="lg" title="Get premium" no-stacking>
                <h4 v-translate>You need to have a premium account to use this feature!</h4>
            </b-modal>
            <div v-if="!loading">
                <table>
                    <tbody>
                        <tr>
                            <td style="display: flex; align-items: baseline;">
                                <h4>Opgaaf intracommunautaire prestaties {{ report.quarter }} {{ report.year }}</h4>
                                &nbsp;
                                <font-awesome-icon id="tooltip-report-title" class="tooltip-icon tooltip-icon--question" icon="fa-regular fa-circle-question" />
                                <b-tooltip :custom-class="'tooltip-padded'" target="tooltip-report-title" triggers="hover" container="#report-container">
                                    <div class="tooltip-title">
                                        {{ $gettext('Info') }}
                                    </div>
                                    <div class="tooltip-text">
                                        {{ $gettext('This report shows all your exports to other EU countries. You need to report these to the tax office. You can do this by logging into the portal of the Belastingdienst, go to "Overige formulieren" and select "Opgaaf intracommunautaire prestaties".') }}
                                    </div>
                                </b-tooltip>
                            </td>
                        </tr>
                        <br>
                        <tr>
                            <th>
                                Fiscale eenheid
                            </th>
                        </tr>
                        <tr>
                            <td class="unsupported">
                                1. Btw-identificatienummer onderneming in fiscale eenheid

                                <font-awesome-icon id="tooltip-q1" class="tooltip-icon tooltip-icon--question" icon="fa-regular fa-circle-question" />
                                <b-tooltip :custom-class="'tooltip-padded'" target="tooltip-q1" triggers="hover" container="#report-container">
                                    <div class="tooltip-title">
                                        {{ $gettext('Info') }}
                                    </div>
                                    <div class="tooltip-text">
                                        {{ $gettext('If you are a fiscal entity comprising of more than one company, you have to indicate what the reporting company is. For almost all Gekko users this is not a relevant question and can be skipped. If you would like to know more about this, please contact your tax advisor.') }}
                                    </div>
                                </b-tooltip>
                            </td>
                        </tr>
                        <br>
                        <tr>
                            <th>
                                Eerdere opgaven corrigeren
                            </th>
                        </tr>
                        <tr>
                            <td class="unsupported">
                                2a. Correctie intracommunautaire leveringen en diensten

                                <font-awesome-icon id="tooltip-q2a" class="tooltip-icon tooltip-icon--question" icon="fa-regular fa-circle-question" />
                                <b-tooltip :custom-class="'tooltip-padded'" target="tooltip-q2a" triggers="hover" container="#report-container">
                                    <div class="tooltip-title">
                                        {{ $gettext('Info') }}
                                    </div>
                                    <div class="tooltip-text">
                                        {{ $gettext('These questions are for correcting previous periods for missing information. Gekko can unfortunatly not see what you did or did not report to the Belastingdienst and therefore this will not be calculated. You can skip this step if you want to.') }}
                                    </div>
                                </b-tooltip>
                            </td>
                        </tr>
                        <tr>
                            <td class="unsupported">
                                2b. Correctie intracommunautaire ABC-leveringen (vereenvoudigde regeling)

                                <font-awesome-icon id="tooltip-q2b" class="tooltip-icon tooltip-icon--question" icon="fa-regular fa-circle-question" />
                                <b-tooltip :custom-class="'tooltip-padded'" target="tooltip-q2b" triggers="hover" container="#report-container">
                                    <div class="tooltip-title">
                                        {{ $gettext('Info') }}
                                    </div>
                                    <div class="tooltip-text">
                                        {{ $gettext('These questions are for correcting previous periods for missing information. Gekko can unfortunatly not see what you did or did not report to the Belastingdienst and therefore this will not be calculated. You can skip this step if you want to.') }}
                                    </div>
                                </b-tooltip>
                            </td>
                        </tr>
                        <br>
                        <tr>
                            <th>
                                Gegevens intracommunautaire prestaties
                            </th>
                        </tr>
                        <tr>
                            <td>
                                3a. Intracommunautaire leveringen en diensten
                            </td>
                        </tr>
                        <br>
                        <tr>
                            <td>
                                <table>
                                    <thead>
                                        <tr>
                                            <td>Regel</td>
                                            <td>Landcode</td>
                                            <td>
                                                Nummer

                                                <font-awesome-icon id="tooltip-q3a3" class="tooltip-icon tooltip-icon--question" icon="fa-regular fa-circle-question" />
                                                <b-tooltip :custom-class="'tooltip-padded'" target="tooltip-q3a3" triggers="hover" container="#report-container">
                                                    <div class="tooltip-title">
                                                        {{ $gettext('Info') }}
                                                    </div>
                                                    <div class="tooltip-text">
                                                        {{ $gettext('This is the VAT number of the customer excluding the country code.') }}
                                                    </div>
                                                </b-tooltip>
                                            </td>
                                            <td>
                                                Leveringen & Diensten

                                                <font-awesome-icon id="tooltip-q3a4" class="tooltip-icon tooltip-icon--question" icon="fa-regular fa-circle-question" />
                                                <b-tooltip :custom-class="'tooltip-padded'" target="tooltip-q3a4" triggers="hover" container="#report-container">
                                                    <div class="tooltip-title">
                                                        {{ $gettext('Info') }}
                                                    </div>
                                                    <div class="tooltip-text">
                                                        {{ $gettext('Gekko does not know whether you delivered goods or services to another country. You need to indicate yourself whether you invoiced goods ("Leveringen") or services ("Diensten").') }}
                                                    </div>
                                                </b-tooltip>
                                            </td>
                                            <td>Customer</td>
                                            <td>Country Name</td>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-if="report.report_lines && report.report_lines.length==0">
                                            <td colspan="4" translate>No EU foreign invoices in this period</td>
                                        </tr>
                                        <div v-show="false">{{ i = 0 }}</div>
                                        <tr v-for="line in report.report_lines" v-bind:key="line.id">
                                            <td>{{ i++ }}</td>
                                            <td>{{ line.country}}</td>
                                            <td>{{ line.customer_vat_number }}</td> <!-- | limitTo: 100 : 2 -->
                                            <td>{{ line.amount | formatNumber }}</td>
                                            <td>{{ line.customer_name }}</td>
                                            <td>{{ line.get_country_display}}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                        <br>
                        <tr>
                            <td class="unsupported">
                                3b. Intracommunautaire ABC-leveringen (vereenvoudigde regeling)

                                <font-awesome-icon id="tooltip-q3b" class="tooltip-icon tooltip-icon--question" icon="fa-regular fa-circle-question" />
                                <b-tooltip :custom-class="'tooltip-padded'" target="tooltip-q3b" triggers="hover" container="#report-container">
                                    <div class="tooltip-title">
                                        {{ $gettext('Info') }}
                                    </div>
                                    <div class="tooltip-text">
                                        {{ $gettext('In 3b you can report any goods that you traded as a "tussenhandelaar". This basically means that the goods you sold in another country are not made or adjusted by you and you are just a middle man. Gekko cannot calculate this as we do not exactly know the nature of your business. This is however fairly uncommon and you can assume this is not the case. If you are not sure, please contact you tax advisor.') }}
                                    </div>
                                </b-tooltip>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>
<script>
import { mapGetters } from "vuex";
import settingsPageSetup from "@/mixins/settingsPageSetup";
import GekkoButton from "@/components/GekkoButton";
import TitleBar from "@/components/TitleBar";
import EUServicesReportService from "@/store/services/euservicesreport.service";
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner";

export default {
    name: "EUServicesReport",
    mixins: [settingsPageSetup],
    components: { TitleBar, GekkoButton, LoadingSpinner },
    data() {
        return {
            pageSettings: {
                title: this.$gettext("Create report"),
                showCompanyPicker: false
            },
            report: [],
            reportsToOverwrite: [],
            startDate: "",
            endDate: "",
            loading: true
        }
    },
    mounted() {
        if(this.isCreateView) {
            if(Object.keys(this.reportInitialValues).length === 0) {
                this.$router.push({ name: 'EUServicesList'})
                return
            } else {
                this.handleSubmit()
            }
        } else {
            EUServicesReportService.get(this.$route.params.id).then((response) => {
                this.report = response.data;
                let report_initial_values = {
                    company_profile: this.currentCompany.id,
                    year: this.report.year,
                    quarter: this.report.quarter,
                };
                this.$store.commit('taxreports/setEUServicesReportInitialValues', report_initial_values);
                this.loading = false;
            })
        }
    },
    computed: {
        isCreateView: function(){
            return this.$route.path.search("create") > -1
        },
        calculatedStatus: function () {
            if (this.isCreateView) return this.$gettext("Calculate");
            return this.$gettext("Recalculate");
        },
        ...mapGetters({
            reportInitialValues: "taxreports/getEUServicesReportInitialValues",
            currentCompany: "main/getCurrentCompany",
            currentUser: "main/getCurrentUser",
            getCompanyId: 'main/getCompanyId'
        })
    },
    filters: {
        formatDate: function (value) {
            var options = { year: "numeric", month: "long", day: "numeric" };
            return new Date(value).toLocaleDateString("en-US", options);
        }
    },
    methods: {
        handleSubmit() {
            this.loading = true;
            var data = {};

            if (this.currentUser.is_premium) {
                if (this.isCreateView) {
                    data['company_profile'] = this.reportInitialValues.company;
                    data['year'] = this.reportInitialValues.year;
                    data['quarter'] = this.reportInitialValues.quarter;
                    let interval = this.dateInterval(this.reportInitialValues.year, this.reportInitialValues.quarter, this.reportInitialValues.month)
                    data['start_date'] = interval.start_date
                    data['end_date'] = interval.end_date
                    if (this.reportInitialValues.month !== null) data['month'] = this.reportInitialValues.month

                    EUServicesReportService.create(data).then((response) => {
                        this.report = response.data;
                        this.loading = false;
                        this.$router.push({ name: 'EUServicesReport', params: {id: response.data.id} })
                    })
                } else {
                    const ConfirmModalConstructor = this.$options.components['confirm-modal']
                    const confirmModalInstance = new ConfirmModalConstructor({ propsData: {
                        title: this.$gettext('Recalculate report'),
                        message: this.$gettext('Are you sure? This will overwrite the current report.'),
                        options: {
                            variant: 'primary',
                            confirmText: this.$gettext('Ok')
                        }
                    }})
                    confirmModalInstance.$mount()
                    document.body.appendChild(confirmModalInstance.$el)
                    confirmModalInstance.$on('confirmed', () => {
                        if (confirmModalInstance && confirmModalInstance.$el.parentNode) {
                            confirmModalInstance.$el.parentNode.removeChild(confirmModalInstance.$el)
                        }
                        data['company_profile'] = this.getCompanyId
                        data['year'] = this.report.year
                        data['quarter'] = this.report.quarter
                        let interval = this.dateInterval(this.report.year, this.report.quarter, this.report.month)
                        data['start_date'] = interval.start_date
                        data['end_date'] = interval.end_date
                        EUServicesReportService.recalculate(this.report.id, data).then((response) => {
                            this.report = response.data;
                            this.loading = false;
                        })
                    })
                    confirmModalInstance.$on('cancel', () => {
                        if (confirmModalInstance && confirmModalInstance.$el.parentNode) {
                            confirmModalInstance.$el.parentNode.removeChild(confirmModalInstance.$el)
                        }
                        this.loading = false;
                    })
                }
            } else {
                this.$store.commit('togglePremiumModal', 'web_reports_vat')
                this.loading = false
            }
        },
        dateInterval(selectedYear, selectedQuarter, selectedMonth){
            var outputDate = [];
            if(!selectedMonth && selectedQuarter) {
                var quarters = {
                    any: {
                        start_date: new Date(selectedYear, 0, 1).toString("yyyy-MM-dd"),
                        end_date: new Date(selectedYear, 11, 31).toString("yyyy-MM-dd")
                    },
                    Q1: {
                        start_date: new Date(selectedYear, 0, 1).toString("yyyy-MM-dd"),
                        end_date: new Date(selectedYear, 2, 31).toString("yyyy-MM-dd")
                    },
                    Q2: {
                        start_date: new Date(selectedYear, 3, 1).toString("yyyy-MM-dd"),
                        end_date: new Date(selectedYear, 5, 30).toString("yyyy-MM-dd")
                    },
                    Q3: {
                        start_date: new Date(selectedYear, 6, 1).toString("yyyy-MM-dd"),
                        end_date: new Date(selectedYear, 8, 30).toString("yyyy-MM-dd")
                    },
                    Q4: {
                        start_date: new Date(selectedYear, 9, 1).toString("yyyy-MM-dd"),
                        end_date: new Date(selectedYear, 11, 31).toString("yyyy-MM-dd")
                    }
                };
                outputDate = quarters[selectedQuarter];
            }
            if(selectedMonth && selectedMonth!=0) {
                selectedMonth -= 1;
                outputDate = {
                    start_date: new Date(selectedYear, selectedMonth, 1).toString("yyyy-MM-dd"),
                    end_date: new Date(selectedYear, selectedMonth + 1, 0).toString("yyyy-MM-dd")
                }
            }
            return outputDate;
        }
    }
}
</script>
<style lang="scss" scoped>
table{
    width: 100%;
}
.report-info {
    align-items: center;
    height: 100%;
    &__msg {
        font-size: 14px;
        line-height: 1;
        .svg-inline--fa { margin-right: 10px; font-size: 16px; }
        &__container {
            display: flex;
            align-items: center;
        }
    }
    &__btn {
        text-align: right;
        font-size: 14px;
    }
}
.warning{
    border: none;
    border-radius: $radiusMd;
    background: $colorWarningBg;
    color: $colorWarningText;
    padding: 15px 0;
    .svg-inline--fa { margin-right: 5px;}
}
.numbers {
    width: 25%;
    text-align: right;
    font-weight: bold;
}
.unsupported{
    color: #ccc;
}
.correction {
    display: none;
    &.text-invoiced, &.text-danger { display: inline-block; }
}
</style>